import React, { useEffect, useRef, useState } from 'react';
import { api, apiError } from '../api/client';
import { downloadFromResponse } from '../lib/download';
import { Button, Dialog, Icon } from './ui';
import './SessionAttachments.css';

const labels = { uploading: 'Загружается', pending: 'Ожидает обработки', processing: 'Обрабатывается', ready: 'Готов', failed: 'Ошибка' };

export function AttachmentPicker({ draft, disabled = false, compact = false, children }) {
  const input = useRef(null);
  const [helpOpen, setHelpOpen] = useState(false);
  return <div className={`attachment-picker${compact ? ' attachment-picker-compact' : ''}`} onDragOver={event => { if (!disabled) event.preventDefault(); }}
    onDrop={event => { event.preventDefault(); if (!disabled) draft.add(event.dataTransfer.files); }}>
    <div className={compact ? 'chat-input-bar' : 'attachment-controls'}>
    <Button disabled={disabled} aria-label="Прикрепить документы" title="Прикрепить документы"
      onClick={() => { setHelpOpen(true); input.current?.click(); }}>{compact ? <Icon name="plus" /> : 'Прикрепить документы'}</Button>
    <input ref={input} type="file" multiple hidden aria-label="Выбрать документы сессии"
      accept=".pdf,.docx,.txt,.md,.xlsx,.csv,.pptx,.png,.jpg,.jpeg,.webp" disabled={disabled}
      onChange={event => { draft.add(event.target.files); setHelpOpen(false); event.target.value = ''; }} />
    {children}
    </div>
    {(!compact || helpOpen) && <span className="text-muted attachment-hint">Или перетащите файлы · PDF, DOCX, TXT/MD, XLSX/CSV, PPTX, PNG/JPEG/WebP · до 25 МиБ</span>}
    {draft.error && <p role="alert">{draft.error}</p>}
    {draft.files.length > 0 && <ul className="attachment-list" aria-live="polite">{draft.files.map(file => <li key={file.key}>
      <div><strong>{file.filename}</strong><span className="text-muted"> · {labels[file.status]}{file.status === 'uploading' ? ` ${file.progress || 0}%` : ''}</span>
        {file.error && <p role="alert">{file.error}</p>}</div>
      <div className="attachment-actions">
        {file.status === 'failed' && <Button disabled={disabled} onClick={() => draft.retry(file)}>Повторить</Button>}
        <Button disabled={disabled} aria-label={`Убрать ${file.filename}`} onClick={() => draft.remove(file)}>Убрать</Button>
      </div>
    </li>)}</ul>}
  </div>;
}

export function AttachmentLinks({ items = [], onExclude, disabled = false }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const previewGeneration = useRef(0);
  const open = async (file, append = false) => {
    const generation = ++previewGeneration.current;
    setBusy(true); setError('');
    try {
      const page = await api.readSessionAttachment(file.id, append ? preview.next_offset : 0);
      if (generation === previewGeneration.current) setPreview(current => append
        ? current?.id === file.id ? { ...page, text: current.text + page.text } : current
        : page);
    } catch (e) { if (generation === previewGeneration.current) setError(apiError(e)); }
    finally { if (generation === previewGeneration.current) setBusy(false); }
  };
  const download = async file => {
    try { downloadFromResponse(await api.downloadSessionAttachment(file.id), file.filename); }
    catch (e) { setError(apiError(e)); }
  };
  return <>
    {error && <p role="alert">{error}</p>}
    {items.length > 0 && <ul className="attachment-list">{items.map(file => <li key={file.id}>
      <div><strong>{file.filename}</strong>{file.excluded && <span className="text-muted"> · исключён из дальнейшей работы</span>}</div>
      <div className="attachment-actions">
        <Button disabled={busy} onClick={() => open(file)}>Посмотреть</Button>
        <Button onClick={() => download(file)}>Скачать</Button>
        {onExclude && !file.excluded && <Button disabled={disabled} onClick={() => onExclude(file)}>Исключить</Button>}
      </div>
    </li>)}</ul>}
    {preview && <Dialog title={preview.filename} onClose={() => { previewGeneration.current += 1; setPreview(null); setBusy(false); }}>
      <pre className="attachment-preview">{preview.text}</pre>
      {preview.next_offset !== null && <Button busy={busy} onClick={() => open(preview, true)}>Показать далее</Button>}
    </Dialog>}
  </>;
}

export default function SessionAttachments({ sessionId, onChange, disabled }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try { const rows = await api.listSessionAttachments(sessionId); if (active) { setItems(rows); setError(''); } }
      catch (e) { if (active) setError(apiError(e)); }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [sessionId]);
  const exclude = async file => {
    setBusy(true); setError('');
    try {
      await api.excludeSessionAttachment(sessionId, file.id);
      setItems(await api.listSessionAttachments(sessionId));
      await onChange?.();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  return <details className="session-attachments"><summary>Документы сессии ({items.filter(f => !f.excluded).length})</summary>
    {error && <p role="alert">{error}</p>}
    {!items.length && <p className="text-muted">Прикрепите документы к сообщению, чтобы агенты использовали их в этой сессии.</p>}
    <AttachmentLinks items={items} onExclude={exclude} disabled={disabled || busy} />
  </details>;
}
